import {
  createEngagementSession,
  openConversation,
  type BotOption,
  type BotTurn,
  type Conversation,
  type SessionContext,
} from '@pypestream/testview/browser';

import { buildEngagementApiUrl } from '../../utils/engagement-api-url';
import {
  checkPatterns,
  findOption,
  parseScript,
  says,
  type RunCheck,
  type ScriptStep,
} from './case-script';
import { webviewOf } from './live-chat';
import { addSubmission } from './webview-submission';
import {
  requestNextTurn,
  requestScore,
  requestSeedTurns,
  requestSendMessage,
} from './validate-api';
import type {
  RunChannel,
  RunScore,
  StartPosition,
  ValidateTurn,
} from './validate-runs';
import { scoreFocus, scoreTurns } from './turn-scoring';

/**
 * A simulated scenario: the model plays the customer, the real solution
 * answers over the Engagement API, and the whole conversation is scored.
 *
 * This is the testing tool's intelligent mode — `EngagementApiTestRunner`
 * driving `ConversationPlanner` turn by turn and handing the log to the
 * evaluator — run from the page, like Test. With a `script` it is the tool's
 * direct or mixed mode instead (`case-script.ts`): the customer's steps are
 * written out, and `ai:` steps hand a goal to the simulated customer.
 */

/** The tester's default budget for an open-ended goal. */
export const SCENARIO_MAX_TURNS = 6;

export type ScenarioStep = 'writing' | 'running' | 'scoring';

export interface ScenarioInput {
  channel: RunChannel;
  description: string;
  expectation?: string;
  maxTurns?: number;
  /** Mid or End replays generated earlier messages first, as seeded context. */
  startAt?: StartPosition;
  /** Written-out steps; replaces the simulated customer when set. */
  script?: string;
  /** The page and customer the session reports (URL parameters, passthrough). */
  context?: SessionContext;
  /** Run the solution's AI turns on this AI Gateway model (`x-ai-eg-model`). */
  model?: string;
  /** Text the replies must, and must never, contain. */
  mustSay?: string[];
  mustNotSay?: string[];
}

export interface ScenarioResult {
  chatId: string;
  transcript: ValidateTurn[];
  startedAt: number;
  endedAt: number;
  score: RunScore;
  /** Each scored bot reply, by transcript index — see `scoreTurns`. */
  turnScores: Record<string, RunScore>;
  /** Why the conversation stopped short, when it did. */
  stoppedBecause: string | null;
  /** Script `expect:` steps and the case's patterns. */
  checks: RunCheck[];
}

export interface ScenarioDeps {
  onStep?: (step: ScenarioStep, transcript: ValidateTurn[]) => void;
  signal?: AbortSignal;
  createSession?: typeof createEngagementSession;
  openConv?: typeof openConversation;
  nextTurn?: typeof requestNextTurn;
  score?: typeof requestScore;
  seed?: typeof requestSeedTurns;
  sendWithModel?: typeof requestSendMessage;
  origin?: string;
}

const toBotTurn = (turn: BotTurn): ValidateTurn => {
  const webview = webviewOf(turn.frames);
  return {
    role: 'bot',
    text: turn.text,
    ...(turn.options.length ? { options: turn.options } : {}),
    ...(turn.latencyMs !== undefined ? { latencyMs: turn.latencyMs } : {}),
    ...(turn.system ? { system: true } : {}),
    ...(webview ? { webview } : {}),
  };
};

/** The persistent menu's options, from whichever frame last carried it. */
const menuOf = (frames: unknown[]): BotOption[] | undefined => {
  let found: BotOption[] | undefined;
  frames.forEach((f) => {
    const app = (f as { app_object?: unknown } | null)?.app_object as
      | { type?: unknown; options?: unknown }
      | undefined;
    if (app?.type !== 'persistent_menu' || !Array.isArray(app.options)) return;
    found = (app.options as { label?: unknown; value?: unknown }[])
      .filter((o) => o.label !== undefined || o.value !== undefined)
      .map((o) => ({
        label: String(o.label ?? o.value),
        value: String(o.value ?? o.label),
      }));
  });
  return found;
};

const errorText = (err: unknown) =>
  err instanceof Error ? err.message : String(err);

export const runScenario = async (
  {
    channel,
    description,
    expectation,
    maxTurns = SCENARIO_MAX_TURNS,
    startAt = 'beginning',
    script,
    context,
    model,
    mustSay,
    mustNotSay,
  }: ScenarioInput,
  {
    onStep = () => undefined,
    signal,
    createSession = createEngagementSession,
    openConv = openConversation,
    nextTurn = requestNextTurn,
    score = requestScore,
    seed = requestSeedTurns,
    sendWithModel = requestSendMessage,
    origin = window.location.origin,
  }: ScenarioDeps = {}
): Promise<ScenarioResult> => {
  const engagementApiUrl = buildEngagementApiUrl(origin, channel.env);
  const startedAt = Date.now();
  const transcript: ValidateTurn[] = [];
  const checks: RunCheck[] = [];
  let stoppedBecause: string | null = null;

  const parsed = script?.trim() ? parseScript(script) : null;
  if (parsed?.errors.length) {
    throw new Error(`The script can’t run: ${parsed.errors.join('; ')}`);
  }

  onStep('writing', transcript);
  // Written before the chat opens, so a failed plan doesn't leave one open.
  // A script says where it starts itself.
  const seedMessages =
    startAt === 'beginning' || parsed
      ? []
      : (
          await seed(
            { scenario: description, expectation, position: startAt },
            signal
          )
        ).messages;

  const session = await createSession({
    appId: channel.appId,
    engagementApiUrl,
    signal,
    context,
  });

  let conversation: Conversation | undefined;
  let menu: BotOption[] | undefined;
  const lastBot = () => [...transcript].reverse().find((t) => t.role === 'bot');
  const record = (reply: BotTurn, extra: Partial<ValidateTurn> = {}) => {
    menu = menuOf(reply.frames) ?? menu;
    transcript.push({ ...toBotTurn(reply), ...extra });
    onStep('running', [...transcript]);
  };

  /** One customer turn: shown, sent, answered. Returns false to stop. */
  const exchange = async (
    shown: string,
    send: (c: Conversation) => Promise<BotTurn>,
    extra: Partial<ValidateTurn> = {}
  ): Promise<boolean> => {
    transcript.push({ role: 'user', text: shown, at: Date.now(), ...extra });
    onStep('running', [...transcript]);
    try {
      record(await send(conversation as Conversation));
      if (conversation?.isEnded()) {
        stoppedBecause = 'The solution ended the conversation.';
        return false;
      }
      return true;
    } catch (err) {
      // The conversation so far is still evidence: score what happened.
      stoppedBecause = errorText(err);
      return false;
    }
  };

  /** The simulated customer, for up to `budget` messages toward `goal`. */
  const simulate = async (goal: string, budget: number) => {
    for (let turn = 1; turn <= budget && !stoppedBecause; turn += 1) {
      if (signal?.aborted) {
        stoppedBecause = 'Stopped.';
        return;
      }
      // eslint-disable-next-line no-await-in-loop
      const plan = await nextTurn(
        { scenario: goal, expectation, transcript, turn, maxTurns: budget },
        signal
      );
      if (plan.done || !plan.message) return;
      const option = plan.pick
        ? lastBot()?.options?.find((o) => o.label === plan.pick)
        : undefined;
      // Strictly sequential: turn N's reply is turn N+1's context.
      // eslint-disable-next-line no-await-in-loop
      const going = await exchange(plan.message, (c) =>
        option ? c.sendOption(option) : c.send(plan.message as string)
      );
      if (!going) return;
    }
  };

  const runStep = async (step: ScriptStep): Promise<boolean> => {
    const c = conversation as Conversation;
    switch (step.kind) {
      case 'say':
        return exchange(step.text, (x) => x.send(step.text));
      case 'click': {
        const option = findOption(lastBot()?.options, step.label);
        if (!option) {
          stoppedBecause = `No button “${step.label}” on the last reply.`;
          return false;
        }
        return exchange(option.label, (x) => x.sendOption(option));
      }
      case 'menu': {
        const option = findOption(menu, step.label);
        if (!option) {
          stoppedBecause = `No “${step.label}” in the persistent menu.`;
          return false;
        }
        return exchange(option.label, (x) => x.sendOption(option));
      }
      case 'ai':
        await simulate(`${description}\n\nThis part: ${step.goal}`, maxTurns);
        return !stoppedBecause;
      case 'expect': {
        const reply = lastBot();
        checks.push({
          label: `Reply says “${step.text}”`,
          passed: !!reply && says(reply.text, step.text),
        });
        return true;
      }
      case 'webview-submit':
      case 'webview-close': {
        const webview = lastBot()?.webview;
        if (!webview) {
          stoppedBecause = 'No webview open to submit or close.';
          return false;
        }
        if (step.kind === 'webview-submit') {
          if (!c.sendMetadata) {
            stoppedBecause = 'This connection can’t send webview data.';
            return false;
          }
          await c.sendMetadata(step.data);
        }
        const message = webview.closeMessage || 'Close';
        return exchange(
          message,
          (x) => (x.sendPlain ? x.sendPlain(message) : x.send(message)),
          step.kind === 'webview-submit'
            ? {
                submitted: addSubmission(
                  null,
                  webview.label || 'the webview',
                  step.data
                ),
              }
            : {}
        );
      }
      default:
        return true;
    }
  };

  try {
    conversation = await openConv({
      appId: channel.appId,
      session,
      engagementApiUrl,
      signal,
      ...(model
        ? {
            sendVia: async (message: Record<string, unknown>) => {
              await sendWithModel(
                {
                  engagementApiUrl,
                  chatId: session.chatId,
                  accessToken: session.accessToken,
                  message,
                  model,
                },
                signal
              );
            },
          }
        : {}),
    });
    onStep('running', transcript);

    const greeting = await conversation.greeting();
    if (greeting && (greeting.text || greeting.options.length)) {
      record(greeting, { seeded: seedMessages.length > 0 });
    }

    // Replay the earlier conversation to the real bot, so the scenario picks
    // up from where a real customer would be.
    for (const message of seedMessages) {
      if (signal?.aborted || stoppedBecause) break;
      transcript.push({
        role: 'user',
        text: message,
        seeded: true,
        at: Date.now(),
      });
      onStep('running', [...transcript]);
      try {
        // eslint-disable-next-line no-await-in-loop
        record(await conversation.send(message), { seeded: true });
      } catch (err) {
        stoppedBecause = `While replaying the earlier conversation: ${errorText(err)}`;
      }
    }

    if (parsed) {
      for (const step of parsed.steps) {
        if (signal?.aborted) {
          stoppedBecause = 'Stopped.';
          break;
        }
        // eslint-disable-next-line no-await-in-loop
        if (stoppedBecause || !(await runStep(step))) break;
      }
    } else {
      await simulate(description, maxTurns);
    }
  } finally {
    await conversation?.end().catch(() => undefined);
  }

  checks.push(...checkPatterns(transcript, { mustSay, mustNotSay }));

  const endedAt = Date.now();
  onStep('scoring', [...transcript]);

  let runScore: RunScore;
  let turnScores: Record<string, RunScore> = {};
  if (!transcript.some((t) => t.role === 'bot' && !t.seeded)) {
    runScore = {
      status: 'error',
      message:
        stoppedBecause ??
        'The solution never replied, so there was nothing to score.',
    };
  } else {
    // The conversation and each of its replies, scored side by side.
    const scoring = { transcript, scenario: description, expectation };
    [runScore, turnScores] = await Promise.all([
      scoreFocus({ ...scoring, focusIndex: null }, { score, signal }),
      scoreTurns(scoring, { score, signal }),
    ]);
  }

  return {
    chatId: session.chatId,
    transcript,
    startedAt,
    endedAt,
    score: runScore,
    turnScores,
    stoppedBecause,
    checks,
  };
};
